<template>
    <div class="search-input-component position-relative w-100">
        <div class="d-flex align-items-center w-100">
            <svg v-if="showIcon" style="height: 36px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                <path d="M0 188.6C0 84.4 86 0 192 0S384 84.4 384 188.6c0 119.3-120.2 262.3-170.4 316.8-11.8 12.8-31.5 12.8-43.3 0-50.2-54.5-170.4-197.5-170.4-316.8zM192 256a64 64 0 1 0 0-128 64 64 0 1 0 0 128z"/>
            </svg>
            <div class="position-relative w-100">
                <input 
                    ref="searchInput"
                    type="text" 
                    :class="inputClass"
                    :placeholder="placeholder || $t('property_detail.search_bar_placeholder') || $t('search_bar_placeholder')"
                    v-model="query"
                    @input="handleSearchInput"
                    @keyup="handleKeyUp"
                    autocomplete="off"
                >
                <i v-if="query"
                    class="fa fa-times-circle position-absolute"
                    @click="clearSearch"
                    style="top: 50%; right: 10px; transform: translateY(-50%); cursor: pointer; z-index: 10;"></i>
                <ul v-if="suggestions && suggestions.length > 0"
                    class="suggestions-list"
                    :style="suggestionsStyle">
                    <li v-for="(suggestion, index) in suggestions"
                        :key="index"
                        @click="selectSuggestion(suggestion)"
                        class="suggestion-item">
                        <div style="margin-right: 10px;">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
                                <path fill="#676767"
                                    d="M8 1C5.236 1 3 3.191 3 5.9 3 9.575 8 15 8 15s5-5.425 5-9.1C13 3.191 10.764 1 8 1M4.429 5.9c0-1.932 1.6-3.5 3.571-3.5s3.571 1.568 3.571 3.5c0 2.016-2.057 5.033-3.571 6.916C6.514 10.947 4.429 7.895 4.429 5.9">
                                </path>
                                <path fill="#676767"
                                    d="M8 7.65c.986 0 1.786-.784 1.786-1.75S8.986 4.15 8 4.15s-1.786.784-1.786 1.75.8 1.75 1.786 1.75">
                                </path>
                            </svg>
                        </div>
                        <span>{{ suggestion.description }}</span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import Vue from 'vue';
import { Loader } from '@googlemaps/js-api-loader';

export default {
    name: 'SearchInput',
    props: {
        placeholder: {
            type: String,
            default: '',
        },
        inputClass: {
            type: String,
            default: 'shadow-none search-input border-0 p-0 w-100'
        },
        showIcon: {
            type: Boolean,
            default: true
        },
        navigateToResults: {
            type: Boolean,
            default: false
        },
        value: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            query: this.value || '',
            suggestions: [],
            placesService: null,
            city: '',
            regionName: '',
            country: '',
            searchQuery: ''
        };
    },
    watch: {
        value(newValue) {
            if (newValue !== this.query) {
                this.query = newValue;
            }
        }
    },
    computed: {
        suggestionsStyle() {
            return `
                position: relative; 
                top: 100%; 
                left: 0; 
                right: 0; 
                background: white; 
                border: 1px solid #ddd; 
                list-style: none; 
                padding: 0; 
                margin: 0; 
                z-index: 1000; 
                max-height: 300px; 
                overflow-y: auto; 
                margin-top: 8px; 
                border-radius: 8px; 
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            `;
        }
    },
    mounted() {
        this.loadPlacesService();
        this.debouncedFetchSuggestions = _.debounce(this.fetchSuggestions, 300);
    },
    methods: {
        async loadPlacesService() {
            const loader = new Loader({
                apiKey: process.env.VUE_APP_GOOLE_PLACES_API_KEY,
                version: 'weekly',
                libraries: ['places'],
                language: 'en'
            });

            try {
                await loader.load();
                this.placesService = new window.google.maps.places.AutocompleteService();
            } catch (error) {
                console.error('Error loading Google Maps API:', error);
            }
        },

        handleSearchInput() {
            this.searchQuery = this.query;
            this.$emit('input', this.query);
            if (this.query.length < 3) {
                this.suggestions = [];
                return;
            }
            this.debouncedFetchSuggestions();
        },

        fetchSuggestions() {
            if (!this.placesService || !this.query) {
                this.suggestions = [];
                return;
            }
            this.placesService.getPlacePredictions(
                { input: this.query, types: [] },
                (predictions, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                        this.suggestions = predictions || [];
                    } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                        this.suggestions = [];
                    } else {
                        console.error('Error fetching place predictions:', status);
                        this.suggestions = [];
                    }
                }
            );
        },

        async selectSuggestion(suggestion) {
            if (!suggestion || !suggestion.description) return;

            const placeService = new window.google.maps.places.PlacesService(document.createElement('div'));
            placeService.getDetails({ placeId: suggestion.place_id }, (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    let city = '';
                    let state = '';
                    let country = '';
                    place.address_components.forEach(component => {
                        if (component.types.includes('locality')) city = component.long_name;
                        if (component.types.includes('administrative_area_level_1')) state = component.long_name;
                        if (component.types.includes('country')) country = component.long_name;
                    });
                    
                    const hasLocality = !!city;
                    const hasState = !!state;
                    const hasCountry = !!country;

                    if (!hasLocality && hasState && hasCountry) {
                        city = '';
                        // state = state;
                        // country = country;
                    } else if (!hasLocality && !hasState && hasCountry) {
                        city = '';
                        state = '';
                        // country = country;
                    } else {
                        city = city || suggestion.description.split(',')[0].trim();
                        state = state || '';
                        country = country || suggestion.description.split(',').pop().trim();
                    }

                    // User requested to search only by state and country, not send city at all
                    const finalState = state || city || suggestion.description.split(',')[0].trim();
                    const finalCountry = country || suggestion.description.split(',').pop().trim();

                    this.city = '';
                    this.regionName = finalState;
                    this.country = finalCountry;
                    this.query = suggestion.description;
                    this.searchQuery = suggestion.description;
                    this.suggestions = [];
                    this.$emit('input', this.query);

                    this.emitSearch();
                } else {
                    console.error('Error fetching place details:', status);
                }
            });
        },

        handleKeyUp(event) {
            if (event.key === 'Enter' && this.query) {
                this.callApiOnEnter();
            }
        },

        callApiOnEnter() {
            if (!this.query) return;
            if (this.suggestions.length > 0) {
                this.selectSuggestion(this.suggestions[0]);
            } else {
                // Manual entry parsing
                const inputValue = this.query.trim();
                const parts = inputValue.split(',').map(part => part.trim());

                let state = '';
                let country = '';

                if (parts.length === 1) {
                    state = parts[0];
                    country = 'United Arab Emirates';
                } else if (parts.length === 2) {
                    state = parts[0];
                    country = parts[1];
                } else if (parts.length >= 3) {
                    state = parts[1] || parts[0];
                    country = parts[2];
                }

                console.log('Manual entry parsed:', { state, country, inputValue });

                this.city = '';
                this.regionName = state;
                this.country = country;
                this.searchQuery = this.query;
                this.$emit('input', this.query);

                this.emitSearch();
            }
        },


        emitSearch() {
            console.log('=== SearchInput emitSearch ===');
            console.log('Data properties:', {
                city: '',
                regionName: this.regionName,
                country: this.country,
                query: this.query,
                navigateToResults: this.navigateToResults
            });

            const payload = {
                city: '',
                state: this.regionName || '',
                country: this.country,
                searchText: this.query,
                navigateToResults: this.navigateToResults
            };

            // If navigating to results, just change route without query params
            if (this.navigateToResults) {
                // Navigate to searchProperty page first
                this.$router.push({ name: 'searchProperty' }).then(() => {
                    // Then emit the search event ONLY on $root to avoid duplicate calls
                    this.$nextTick(() => {
                        this.$root.$emit('search-initiated', payload);
                    });
                });
            } else {
                // Emit event ONLY on $root to avoid duplicate calls
                this.$root.$emit('search-initiated', payload);
            }
            
            console.log('Event emitted with payload:', {
                city: this.city,
                state: this.regionName,
                country: this.country,
                searchText: this.query,
                navigateToResults: this.navigateToResults
            });
        },

        clearSearch() {
            this.query = '';
            this.searchQuery = '';
            this.city = '';
            this.regionName = '';
            this.country = '';
            this.suggestions = [];
            this.$emit('input', this.query);
            
            // Emit clear event
            this.$root.$emit('search-cleared');
            this.closeModal();

        }
    }
};
</script>

<style scoped>
.search-input-component {
    width: 100%;
}

.suggestions-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    list-style: none;
    padding: 0;
    margin: 0;
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
    margin-top: 8px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.suggestion-item {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
}

.suggestion-item:hover {
    background-color: #f5f5f5;
}

.suggestion-item:last-child {
    border-bottom: none;
}
</style>
